import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import moment from "moment";
import BusMarkupModal from "./BusMarkupModal";
import {
  RiCheckboxCircleLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiCalendarLine,
  RiArrowLeftRightLine,
  RiArrowLeftLine,
  RiErrorWarningLine,
  RiMoneyDollarCircleLine,
  RiFileList3Line,
  RiDownloadLine,
  RiShieldCheckLine,
  RiInformationLine,
  RiTicketLine,
  RiEditLine,
  RiRouteLine,
  RiArrowDownSLine
} from "react-icons/ri";

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passengerData, setPassengerData] = useState([]);
  const [seatData, setSeatData] = useState([]);
  const [boardingPointDisplay, setBoardingPointDisplay] = useState("");
  const [droppingPointDisplay, setDroppingPointDisplay] = useState("");
  const [boardingTime, setBoardingTime] = useState("");
  const [droppingTime, setDroppingTime] = useState("");
  const [showMarkupModal, setShowMarkupModal] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);

  const dropdownRef = useRef(null);
  const paymentSummary = () => {
    const baseFare = booking?.baseFare || booking?.totalFare || 0;
    const totalPrice = booking?.totalPrice || 0;
    const platformFee = Math.round(totalPrice - baseFare - (booking?.convenienceFee || 0) - (booking?.markup || 0) - (booking?.paymentGatewayAmount || 0) + (booking?.discount || 0));
    const convenienceFee = Math.round(booking?.convenienceFee || 0) + Math.round(booking?.markup || 0);
    const paymentGatewayAmount = Math.round(booking?.paymentGatewayAmount || 0);
    const discount = Math.round(booking?.discount || 0);
    const totalAmount = Math.round(booking?.totalPrice || 0);

    return {
      baseFare,
      platformFee,
      convenienceFee,
      paymentGatewayAmount,
      discount,
      totalAmount
    };
  };
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return timeStr.replace(/:00(?=\s|$)/g, "");
  };

  const parseLocationData = (locationString) => {
    if (!locationString) return "";

    try {
      if (typeof locationString === 'string' && !locationString.startsWith('{')) {
        return locationString;
      }

      const parsed = JSON.parse(locationString);
      if (parsed.Location) {
        return parsed.Location;
      }
      return locationString;
    } catch (e) {
      return locationString;
    }
  };

  const isPastDeparture = () => {
    if (!booking?.departureDate) return false;
    const departureDate = moment(booking.departureDate);
    return departureDate.isBefore(moment(), 'day');
  };

  const isBookingBooked = () => {
    return booking?.bookingStatus?.toUpperCase() === "BOOKED";
  };

  const isSource = () => {
    const source = booking?.source?.toUpperCase();
    return source === "CUSTOMER" || source === "CORPORATE";
  };

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchBookingDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get(
        `${config.GETBUSBOOKINGBYID}/${bookingId}`
      );

      if (response) {
        setBooking(response);

        if (response.boardingPoint) {
          setBoardingPointDisplay(parseLocationData(response.boardingPoint));
          try {
            const parsed = JSON.parse(response.boardingPoint);
            if (parsed.Time) {
              setBoardingTime(formatTime(parsed.Time));
            }
          } catch (e) {
            console.error("Error parsing boarding time:", e);
          }
        }

        if (response.droppingPoint) {
          setDroppingPointDisplay(parseLocationData(response.droppingPoint));
          try {
            const parsed = JSON.parse(response.droppingPoint);
            if (parsed.Time) {
              setDroppingTime(formatTime(parsed.Time));
            }
          } catch (e) {
            console.error("Error parsing dropping time:", e);
          }
        }

        if (response.passengerDetails) {
          try {
            const parsedPassengers = JSON.parse(response.passengerDetails);
            setPassengerData(
              Array.isArray(parsedPassengers)
                ? parsedPassengers
                : [parsedPassengers]
            );
          } catch (e) {
            console.error("Error parsing passenger details:", e);
            setPassengerData([]);
          }
        }

        if (response.seatDetails) {
          try {
            const parsedSeats = JSON.parse(response.seatDetails);
            setSeatData(
              Array.isArray(parsedSeats) ? parsedSeats : [parsedSeats]
            );
          } catch (e) {
            console.error("Error parsing seat details:", e);
            setSeatData([]);
          }
        }
      } else {
        setError("Booking details not found");
      }
    } catch (err) {
      setError("Failed to fetch booking details. Please try again.");
      console.error("Fetch booking error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const formatCurrency = (amount) => {
    return `INR ${amount}`;
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("DD MMM YYYY");
  };

  const handleViewVoucher = () => {
    navigate(`/bus/voucher/${bookingId}`);
  };

  const handleCustomerInvoice = () => {
    navigate(`/bus/customer-invoice/${bookingId}`);
  };

  const handleAgentInvoice = () => {
    navigate(`/bus/agent-invoice/${bookingId}`);
  };

  const handleBusDownload = () => {
    navigate(`/bus/download/${bookingId}`);
  };

  const handleMarkupUpdate = (updatedBooking) => {
    setBooking(updatedBooking);
  };

  const toggleDownloadDropdown = () => {
    setShowDownloadDropdown(!showDownloadDropdown);
  };

  if (loading) {
    return (
      <div id="layout-wrapper">
        <Header User={user} />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5>Loading Booking Details...</h5>
                      <p className="text-muted">Please wait while we fetch your booking information</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  const paymentSummaryValues = paymentSummary();
  if (error || !booking) {
    return (
      <div id="layout-wrapper">
        <Header User={user} />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <div className="mb-4">
                        <RiCheckboxCircleLine className="text-danger fs-1" />
                      </div>
                      <h4 className="text-danger mb-3">
                        {error || "Booking not found"}
                      </h4>
                      <p className="text-muted mb-4">
                        The booking you're looking for doesn't exist or you don't have permission to view it.
                      </p>
                      <button onClick={handleBack} className="btn btn-primary">
                        <RiArrowLeftLine className="me-2" />
                        Back to Bookings
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Back Button & Action Buttons - Top */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 d-print-none">
                  <button onClick={handleBack} className="btn btn-outline-primary d-flex align-items-center gap-2">
                    <RiArrowLeftLine /> Back to Bookings
                  </button>
                  <div className="d-flex flex-wrap gap-2">
                    {/* Download Dropdown */}
                    <div className="dropdown" ref={dropdownRef}>
                      <button
                        className="btn btn-success d-flex align-items-center gap-2 dropdown-toggle"
                        onClick={toggleDownloadDropdown}
                        aria-expanded={showDownloadDropdown}
                      >
                        <RiDownloadLine /> Download

                      </button>
                      {showDownloadDropdown && (
                        <ul className="dropdown-menu show" style={{ position: 'absolute', inset: '0px auto auto 0px', margin: '0px', transform: 'translate(0px, 40px)' }}>
                          <li>
                            <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => { handleBusDownload(); setShowDownloadDropdown(false); }}>
                              <RiDownloadLine />Download
                            </button>
                          </li>
                          {isBookingBooked() && (
                            <>
                              <li>
                                <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => { handleCustomerInvoice(); setShowDownloadDropdown(false); }}>
                                  <RiFileList3Line /> Customer Invoice
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => { handleAgentInvoice(); setShowDownloadDropdown(false); }}>
                                  <RiFileList3Line /> Agent Invoice
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => { handleViewVoucher(); setShowDownloadDropdown(false); }}>
                                  <RiTicketLine /> Voucher
                                </button>
                              </li>
                            </>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Details Content */}
            <div id="booking-details">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="row">
                    {/* Status Header */}
                    <div className="col-12">
                      <div className={`alert alert-${booking.bookingStatus === "BOOKED" ? "success" : "danger"} border-0 shadow-sm mb-4`}>
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <div className="d-flex align-items-center gap-3">
                              <div className="bg-white rounded-circle p-3">
                                <RiCheckboxCircleLine className={`fs-2 ${booking.bookingStatus === "BOOKED" ? "text-success" : "text-danger"}`} />
                              </div>
                              <div>
                                <h4 className={`mb-1 ${booking.bookingStatus === "BOOKED" ? "text-success" : "text-danger"}`}>
                                  {booking.bookingStatus === "BOOKED" ? "Bus Booking Confirmed!" : booking.bookingStatus}
                                </h4>
                                <div className="d-flex gap-3">
                                  <p className="text-muted mb-0">
                                    Booking RefNo: {booking.bookingNo}
                                  </p>
                                  <span className="text-muted">|</span>
                                  <p className="text-muted mb-0">
                                    PNR: {booking.pnr || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4 text-end d-none d-md-block">
                            <small className="d-block text-muted"><RiShieldCheckLine className="me-1" /> Secure Booking</small>
                            <small className="text-muted">Generated: {formatDate(booking.bookingDate)}</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Left Column - Journey & Passenger Details */}
                    <div className="col-lg-8">
                      {/* Journey Overview Card */}
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white border-bottom-0 pb-0">
                          <div className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-3 d-flex align-items-center gap-2">
                              <RiBusLine className="text-primary" />
                              Journey Overview
                            </h4>
                            {!isPastDeparture() && !isSource() && (
                              <button className="btn btn-warning btn-sm d-flex align-items-center gap-2 text-white d-print-none" onClick={() => setShowMarkupModal(true)}>
                                <RiEditLine /> Edit Markup
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="row align-items-center mb-4">
                            <div className="col-md-5 text-center">
                              <div className="bg-light rounded p-3">
                                <h5 className="text-primary mb-1">{booking.sourceCity}</h5>
                                <p className="text-muted mb-0">
                                  {boardingTime || (booking.boardingTime ? formatTime(booking.boardingTime) : (booking.bordingDate ? formatTime(booking.bordingDate) : ""))}
                                </p>
                                <small className="text-muted">
                                  <RiMapPinLine className="me-1" />
                                  {boardingPointDisplay.split(',')[0] || "Boarding Point"}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-2 text-center">
                              <RiArrowLeftRightLine className="text-muted fs-2" />
                              <div className="bg-primary text-white rounded-pill px-3 py-1 small">Direct</div>
                            </div>
                            <div className="col-md-5 text-center">
                              <div className="bg-light rounded p-3">
                                <h5 className="text-success mb-1">{booking.destinationCity}</h5>
                                <p className="text-muted mb-0">
                                  {droppingTime || (booking.arrivalTime ? formatTime(booking.arrivalTime) : (booking.departureDate ? formatTime(booking.departureDate) : ""))}
                                </p>
                                <small className="text-muted">
                                  <RiMapPinLine className="me-1" />
                                  {droppingPointDisplay.split(',')[0] || "Dropping Point"}
                                </small>
                              </div>
                            </div>
                          </div>

                          <div className="row">
                            {/* Bus Operator */}
                            <div className="col-md-3">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiBusLine className="text-muted" />
                                <div>
                                  <small className="text-muted">Bus Operator</small>
                                  <p className="mb-0 fw-semibold">
                                    {booking.operatorName || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Bus Type */}
                            <div className="col-md-3">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiBusLine className="text-muted" />
                                <div>
                                  <small className="text-muted">Bus Type</small>
                                  <p className="mb-0 fw-semibold">
                                    {booking.busType || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Journey Type */}
                            <div className="col-md-3">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiRouteLine className="text-muted" />
                                <div>
                                  <small className="text-muted">Journey Type</small>
                                  <p className="mb-0 fw-semibold">
                                    {booking.journeyType || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Travel Date */}
                            <div className="col-md-3">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiCalendarLine className="text-muted" />
                                <div>
                                  <small className="text-muted">Travel Date</small>
                                  <p className="mb-0 fw-semibold">
                                    {formatDate(booking.departureDate)}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Passengers */}
                            <div className="col-md-4">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiUserLine className="text-muted" />
                                <div>
                                  <small className="text-muted">Passengers</small>
                                  <p className="mb-0 fw-semibold">
                                    {booking.noOfPassengers || passengerData.length || 1} Passenger(s)
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Booking Status */}
                            <div className="col-md-4">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiFileList3Line className="text-muted" />
                                <div>
                                  <small className="text-muted">Booking Status</small>
                                  <p className="mb-0 fw-semibold">
                                    <span
                                      className={`badge ${booking.bookingStatus === "BOOKED"
                                          ? "bg-success"
                                          : booking.bookingStatus === "FAILED"
                                            ? "bg-danger"
                                            : "bg-warning"
                                        }`}
                                    >
                                      {booking.bookingStatus}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Booking Date */}
                            <div className="col-md-4">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <RiCalendarLine className="text-muted" />
                                <div>
                                  <small className="text-muted">Booking Date</small>
                                  <p className="mb-0 fw-semibold">
                                    {formatDate(booking.bookingDate)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Boarding & Dropping Points */}
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white border-bottom-0">
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <RiMapPinLine className="text-primary" />
                            Pickup & Drop Points
                          </h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="border-start border-3 border-primary ps-3">
                                <h6 className="text-primary mb-2">Boarding Point</h6>
                                <p className="fw-semibold mb-1">{boardingPointDisplay}</p>
                                <p className="text-muted small mb-0">Boarding Time: {boardingTime}</p>
                                <p className="text-muted small mb-0">Report 30 minutes before departure</p>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="border-start border-3 border-success ps-3">
                                <h6 className="text-success mb-2">Dropping Point</h6>
                                <p className="fw-semibold mb-1">{droppingPointDisplay}</p>
                                <p className="text-muted small mb-0">Drop Time: {droppingTime}</p>
                                <p className="text-muted small mb-0">Estimated arrival time</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Passenger Details */}
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-bottom-0">
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <RiUserLine className="text-success" />
                            Passenger Details
                          </h5>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Sr. No</th>
                                  <th>Passenger Name</th>
                                  <th>Seat No</th>
                                  <th>Age</th>
                                  <th>ID Proof</th>
                                </tr>
                              </thead>
                              <tbody>
                                {passengerData.length > 0 ? (
                                  passengerData.map((passenger, index) => {
                                    const seatInfo = seatData[index] || {};
                                    return (
                                      <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>
                                          <strong>{passenger.Title} {passenger.Name}</strong>
                                          <div className="small text-muted">{passenger.Mobile} • {passenger.Email}</div>
                                        </td>
                                        <td>
                                          <span className="badge bg-primary fs-6">
                                            {passenger.SeatNbr || seatInfo.SeatNumber || "N/A"}
                                          </span>
                                        </td>
                                        <td>{passenger.Age || "N/A"}</td>
                                        <td>{passenger.IdType || passenger.IDType || "N/A"}</td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td>1</td>
                                    <td>
                                      <strong>{booking.passengerName || "N/A"}</strong>
                                      <div className="small text-muted">{booking.contactNumber} • {booking.email}</div>
                                    </td>
                                    <td><span className="badge bg-primary fs-6">N/A</span></td>
                                    <td>N/A</td>
                                    <td>N/A</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Right Column - Fare Summary & Important Info */}
                    <div className="col-lg-4">
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white border-bottom-0">
                          <h5 className="mb-0 d-flex align-items-center gap-2">
                            <RiMoneyDollarCircleLine className="text-info" />
                            Fare Summary
                          </h5>
                        </div>
                        <div className="card-body">
                          <div className="fare-summary">
                            {/* Bus Fare */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div><span className="text-muted">Bus Fare</span></div>
                              <span className="fw-semibold">{formatCurrency(paymentSummaryValues.baseFare)}</span>
                            </div>

                            {/* Platform Fee */}

                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                <span className="text-muted">Platform Fee</span>
                                <div className="small text-muted">include 18% GST</div>
                              </div>
                              <span className="fw-semibold">{formatCurrency(paymentSummaryValues.platformFee)}</span>
                            </div>


                            {/* Convenience Fee */}
                            {paymentSummaryValues.convenienceFee > 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div><span className="text-muted">Convenience Fee</span></div>
                                <span className="fw-semibold text-success">{formatCurrency(paymentSummaryValues.convenienceFee)}</span>
                              </div>
                            )}

                            {/* Payment Gateway Fee */}
                            {paymentSummaryValues.paymentGatewayAmount > 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div><span className="text-muted">Payment Gateway Fee</span></div>
                                <span className="fw-semibold">{formatCurrency(Math.round(paymentSummaryValues.paymentGatewayAmount))}</span>
                              </div>
                            )}

                            {/* Discount */}
                            {paymentSummaryValues.discount > 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div><span className="text-muted">Discount</span></div>
                                <span className="fw-semibold text-success">-{formatCurrency(paymentSummaryValues.discount)}</span>
                              </div>
                            )}

                            <hr className="my-3" />

                            {/* Total Amount */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <strong className="fs-5">Total Amount</strong>
                              <strong className="fs-5 text-primary">{formatCurrency(paymentSummaryValues.totalAmount)}</strong>
                            </div>

                            {/* Payment Status */}
                            <div className={`alert ${booking.paymentStatus?.toUpperCase() === "SUCCESS" || booking.paymentStatus?.toUpperCase() === "PAID" ? "alert-success" : "alert-danger"} mb-0 mt-3`}>
                              <div className="d-flex align-items-center gap-2">
                                <RiCheckboxCircleLine className={booking.paymentStatus?.toUpperCase() === "SUCCESS" || booking.paymentStatus?.toUpperCase() === "PAID" ? "text-success" : "text-danger"} />
                                <div>
                                  <strong className="d-block">Payment: {booking.paymentStatus || "PENDING"}</strong>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Important Information */}
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-bottom-0">
                          <h6 className="mb-0 text-warning d-flex align-items-center gap-2">
                            <RiErrorWarningLine />
                            Travel Guidelines
                          </h6>
                        </div>
                        <div className="card-body">
                          <ul className="list-unstyled small mb-0">
                            <li className="mb-2 d-flex align-items-start gap-2">
                              <RiInformationLine className="text-muted mt-1" />
                              <span>Arrive at boarding point 30 minutes before departure</span>
                            </li>
                            <li className="mb-2 d-flex align-items-start gap-2">
                              <RiShieldCheckLine className="text-muted mt-1" />
                              <span>Carry valid government-issued photo ID proof</span>
                            </li>
                            <li className="mb-2 d-flex align-items-start gap-2">
                              <RiDownloadLine className="text-muted mt-1" />
                              <span>Download e-ticket for verification</span>
                            </li>
                            <li className="mb-0 d-flex align-items-start gap-2">
                              <RiBusLine className="text-muted mt-1" />
                              <span>Contact operator for journey-related queries</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>


                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Markup Modal */}
      <BusMarkupModal
        isOpen={showMarkupModal}
        onClose={() => setShowMarkupModal(false)}
        booking={booking}
        onMarkupUpdate={handleMarkupUpdate}
        title="Update Bus Markup"
        buttonText="Update Markup"
      />

      {/* Styles */}
      <style>{`
        .dropdown-menu {
          min-width: 200px;
          padding: 0.5rem 0;
          border-radius: 0.5rem;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        }

        .dropdown-item {
          padding: 0.5rem 1rem;
          cursor: pointer;
        }

        .dropdown-item:hover {
          background-color: #f8f9fa;
        }
        
        /* Payment Status Colors */
        .alert-success {
          background-color: #d4edda !important;
          border-color: #c3e6cb !important;
          color: #155724 !important;
        }
        
        .alert-warning {
          background-color: #fff3cd !important;
          border-color: #ffeeba !important;
          color: #856404 !important;
        }
        
        .text-success {
          color: #28a745 !important;
        }
        
        .text-warning {
          color: #ffc107 !important;
        }
      `}</style>
    </div>
  );
};

export default BookingDetails;